import * as React from 'react';
import { Text } from '@components/base/display';
import { generateId, InputMessage } from '@components/base/inputs/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Size variants for the TextArea.
 * Controls vertical padding (not height, since textarea grows with content).
 * - sm: compact padding
 * - md: default padding
 * - lg: comfortable padding
 */
export type TextAreaSize = 'sm' | 'md' | 'lg';

/**
 * CSS resize behaviour for the textarea element.
 */
export type TextAreaResize = 'none' | 'vertical' | 'horizontal' | 'both';

/**
 * TextArea component props.
 * Extends native textarea attributes, omitting 'size' to avoid conflict
 * with our size prop.
 */
export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  /** Label text displayed above the textarea */
  label?: string;
  /** Helper text displayed below the textarea */
  helperText?: string;
  /** Whether the textarea is in an error state */
  error?: boolean;
  /** Error message displayed when error is true. Replaces helperText when present. */
  errorMessage?: string;
  /** Whether the field is required. Shows a red asterisk next to the label. */
  required?: boolean;
  /** Whether the textarea is disabled */
  disabled?: boolean;
  /** Whether the textarea is read-only */
  readOnly?: boolean;
  /**
   * Size variant controlling padding.
   * @default 'md'
   */
  size?: TextAreaSize;
  /**
   * Number of visible text rows.
   * @default 3
   */
  rows?: number;
  /** Maximum character count. When set the native maxLength attribute is applied. */
  maxLength?: number;
  /**
   * CSS resize behaviour.
   * @default 'vertical'
   */
  resize?: TextAreaResize;
}

// ---------------------------------------------------------------------------
// Padding per size variant (vertical + horizontal)
// ---------------------------------------------------------------------------

/**
 * Padding classes for each size variant.
 * Unlike TextField, TextArea uses vertical padding instead of a fixed height
 * so the element can grow with content.
 */
const sizePaddingClasses: Record<TextAreaSize, string> = {
  sm: 'px-3 py-1.5',
  md: 'px-3 py-2',
  lg: 'px-3 py-2.5',
};

// ---------------------------------------------------------------------------
// Resize utility map
// ---------------------------------------------------------------------------

const resizeClasses: Record<TextAreaResize, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  horizontal: 'resize-x',
  both: 'resize',
};

// ---------------------------------------------------------------------------
// State helpers (mirrors TextField logic)
// ---------------------------------------------------------------------------

/**
 * Builds the state-dependent CSS classes for the textarea element.
 * Extracted to reduce cognitive complexity of the main render function.
 */
function getStateClasses(error: boolean, disabled: boolean): string {
  if (disabled) {
    return [
      'bg-secondary-100',
      'text-secondary-400',
      'border-secondary-300',
      'cursor-not-allowed',
    ].join(' ');
  }

  if (error) {
    return [
      'border-error-700',
      'focus:ring-2',
      'focus:ring-error-700/20',
    ].join(' ');
  }

  return [
    'border-secondary-300',
    'focus:border-primary-400',
    'focus:ring-2',
    'focus:ring-primary-200',
  ].join(' ');
}

/**
 * Builds the complete className string for the textarea element.
 * Extracted to reduce cognitive complexity of the main render function.
 */
function buildTextAreaClasses(
  size: TextAreaSize,
  error: boolean,
  disabled: boolean,
  resize: TextAreaResize,
  className: string,
): string {
  return [
    // Sizing
    sizePaddingClasses[size],
    // Base styling (matches TextField)
    'w-full',
    'rounded-[6px]',
    'border',
    'text-[15px]',
    'leading-[22px]',
    'outline-none',
    'transition-all',
    'duration-150',
    'ease-out',
    // Placeholder
    'placeholder:text-secondary-400',
    // State-dependent classes
    getStateClasses(error, disabled),
    // Resize
    resizeClasses[resize],
    // Additional classes
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TextArea component for multi-line text input with label, helper text,
 * and error states.
 *
 * Built to match the TextField design system styling exactly:
 * - Border: 1px neutral-300 (#D4D1CC)
 * - Border radius: 6px
 * - Padding: 12px horizontal, variable vertical per size
 * - Font: text-base (15px)
 * - Focus: 2px ring primary-200 (#BCCCDC), border primary-400 (#829AB1)
 * - Error: Border error (#B91C1C), ring error/20%
 * - Disabled: Background neutral-100 (#F5F4F2), text neutral-400 (#A8A29E)
 *
 * @example
 * // Basic usage
 * <TextArea label="Description" placeholder="Enter a description" />
 *
 * @example
 * // With error state
 * <TextArea
 *   label="Bio"
 *   error
 *   errorMessage="Bio must be at least 10 characters"
 * />
 *
 * @example
 * // With character limit
 * <TextArea label="Notes" maxLength={500} rows={5} />
 */
const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      helperText,
      error = false,
      errorMessage,
      required = false,
      disabled = false,
      readOnly = false,
      size = 'md',
      rows = 3,
      maxLength,
      resize = 'vertical',
      className = '',
      id: providedId,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    // Generate stable IDs for accessibility
    const textareaId = React.useMemo(
      () => providedId || generateId('textarea'),
      [providedId],
    );
    const helperId = React.useMemo(() => generateId('textarea-helper'), []);

    // Determine message ID for aria-describedby
    const message = (error && errorMessage) ? errorMessage : helperText;
    const messageId = message ? helperId : undefined;

    // Combine aria-describedby if both provided and generated
    const computedAriaDescribedBy =
      [ariaDescribedBy, messageId].filter(Boolean).join(' ') || undefined;

    // Build textarea classes using extracted helper
    const textareaClasses = buildTextAreaClasses(
      size,
      error,
      disabled,
      resize,
      className,
    );

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <Text
            as="label"
            htmlFor={textareaId}
            size="sm"
            weight="medium"
            className="mb-1.5 block text-secondary-700"
          >
            {label}
            {required && (
              <span className="ml-0.5 text-error-700" aria-hidden="true">
                *
              </span>
            )}
          </Text>
        )}

        {/* Textarea */}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={error}
          aria-required={required}
          aria-describedby={computedAriaDescribedBy}
          className={textareaClasses}
          {...props}
        />

        {/* Helper text or error message */}
        <InputMessage
          error={error}
          errorMessage={errorMessage}
          helperText={helperText}
          helperId={helperId}
        />
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';

export { TextArea };
